import { Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  NG_VALUE_ACCESSOR,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { FormService } from '../../form.service';
import { FormComponent } from '../../interfaces/form.interfaces';
import { BaseControlValueAccessorComponent } from '../base-control-value-accessor/base-control-value-accessor.component';

@Component({
  selector: 'app-form-table',
  templateUrl: './form-table.component.html',
  styleUrls: ['./form-table.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormTableComponent),
      multi: true,
    },
  ],
})
export class FormTableComponent extends BaseControlValueAccessorComponent implements OnInit, OnDestroy {
  @Input() title = 'lorem ipsum';
  @Input() columns: any[] = ['text', 'text', 'select', 'datePicker', 'checkBox', 'select', 'select'];
  @Input() tableId = '';
  @Input() columnTitles: string[] = [];
  @Input() minColumn = 1;
  @Input() maxColumn = 4;
  @Input() formControl = new UntypedFormControl();
  isTableBig = true;
  selectedRow: number | null = null;
  tableFormGroup = new UntypedFormGroup({
    rows: new UntypedFormArray([]),
  });
  formLength: number | null = null;
  subscription: Subscription | null = null;

  constructor(public formService: FormService) {
    super();
  }

  ngOnInit(): void {
    this.columnTitles = this.columns.map((c) => {
      return c['title'];
    });
    if (this.columnTitles.length < 3) {
      this.isTableBig = false;
    }
    this.fillFormRows();
    this.listenToFormValueChanges();
    if (this.formControl.value) {
      return;
    }
    this.pushNewRowToFormArray();
    this.removeRowFromFormArrayAt(1);
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    const val = this.tableFormGroup.value;
    const values = this.getValueFromObjectThenPushIntoArray(val.rows);
    const mappedValues: any = [];
    values.forEach((rows: any[]) => {
      const newObject = {};
      rows.forEach((cValue, index) => {
        this.mapItemToDesiredObject(cValue, this.columns[index], newObject);
      });
      mappedValues.push(newObject);
    });
    this.formService.formTableMaps.set(this.tableId, JSON.stringify(mappedValues));
  }
  createValidatorArray(component: FormComponent): ValidatorFn[] {
    const validators = [];
    if (component?.required) {
      validators.push(Validators.required);
    }
    if (component?.regex) {
      validators.push(Validators.pattern(component.regex));
    }
    if (component?.maximumCharacterLimit) {
      validators.push(Validators.maxLength(component.maximumCharacterLimit));
    }
    if (component?.minimumCharacterLimit) {
      validators.push(Validators.minLength(component.minimumCharacterLimit));
    }
    return validators;
  }
  getStartingValue(type: string): string | any[] | boolean {
    if (type === 'dropdown_select') {
      return [];
    }
    if (type === 'checkbox') {
      return false;
    }
    return '';
  }
  pushNewRowToFormArray(valuesToFillColumnWith?: any[]): void {
    const formArray = this.tableFormGroup.get('rows') as UntypedFormArray;
    const fg = new UntypedFormGroup({});
    let id = 0;
    for (const column of this.columns) {
      let startingValue = this.getStartingValue(column.type);
      if (valuesToFillColumnWith) {
        startingValue = valuesToFillColumnWith[column.id]?.choices
          ? valuesToFillColumnWith[column.id].choices
          : valuesToFillColumnWith[column.id];
      }
      const validators = this.createValidatorArray(column);
      fg.addControl(`control${id}`, new UntypedFormControl(startingValue, validators));
      id++;
    }
    formArray.push(fg);
    this.formLength = formArray.length;
  }

  fillFormRows(): void {
    let minColumn = this.minColumn;
    let controlValue;
    if (this.formControl.value) {
      controlValue = this.formControl.value;
    } else {
      const tableValue = this.formService.formTableMaps.get(this.tableId);
      if (tableValue) {
        controlValue = JSON.parse(tableValue);
      }
    }

    if (controlValue?.length) {
      minColumn = controlValue.length;
    }
    if (controlValue) {
      for (let i = 0; i < minColumn; i++) {
        this.pushNewRowToFormArray(controlValue[i]);
      }
    } else {
      for (let i = 0; i < minColumn; i++) {
        this.pushNewRowToFormArray();
      }
    }
  }
  listenToFormValueChanges(): void {
    this.subscription = this.tableFormGroup.valueChanges.subscribe((val) => {
      if (!this.tableFormGroup.valid) {
        this.formControl.setValue(null);
        return;
      }
      const values = this.getValueFromObjectThenPushIntoArray(val.rows);
      const mappedValues: any = [];
      values.forEach((rows: any[]) => {
        const newObject = {};
        rows.forEach((cValue, index) => {
          this.mapItemToDesiredObject(cValue, this.columns[index], newObject);
        });
        mappedValues.push(newObject);
      });
      this.formControl.setValue(mappedValues);
    });
  }
  mapItemToDesiredObject(cValue: any, column: any, objectToWrite: any): any {
    switch (column.type) {
      case 'checkbox':
        objectToWrite[column.id] = cValue;
        break;
      case 'simple_input':
        objectToWrite[column.id] = cValue ? cValue : null;
        break;
      case 'date_picker':
        objectToWrite[column.id] = cValue ? cValue.replace('T', ' ') : null;
        break;
      case 'dropdown_select': {
        objectToWrite[column.id] = {
          choices: cValue.length > 0 ? cValue : null,
          extraChoices: cValue.length > 0 ? [] : null,
        };
      }
    }
  }
  getValueFromObjectThenPushIntoArray(formGroupValueArray: any[]): any[] {
    const stringValuesExtractedFromForm: any = [];
    formGroupValueArray.forEach((v) => {
      const keys = Object.keys(v);
      const valuesFromFg: any = [];
      keys.forEach((k) => {
        valuesFromFg.push(v[k]);
      });
      stringValuesExtractedFromForm.push(valuesFromFg);
    });
    return stringValuesExtractedFromForm;
  }

  getFormArray(): UntypedFormArray {
    return this.tableFormGroup.get('rows') as UntypedFormArray;
  }

  getControlAsFormControl(formGroup: AbstractControl, controlName: string): UntypedFormControl {
    return formGroup.get(controlName) as UntypedFormControl;
  }

  removeRowFromFormArrayAt(i: number): void {
    const formArray = this.getFormArray();
    formArray.removeAt(i);
    this.formLength = formArray.length;
  }

  isSelected(formGroup: AbstractControl, controlName: string, optionValue: string): boolean {
    const fcValue = formGroup.get(controlName)?.value;
    if (fcValue === '') {
      return false;
    }
    return fcValue?.some((option: any) => option.id === optionValue) || false;
  }

  select(formGroup: AbstractControl, controlName: string, optionValue: any): void {
    const control = formGroup.get(controlName);
    let fcValue = control?.value;
    if (fcValue === '') {
      fcValue = [];
    }
    if (fcValue.some((option: any) => option.id === optionValue.id)) {
      fcValue = fcValue.filter((options: any) => options !== optionValue);
    } else {
      fcValue.push(optionValue);
    }
    control?.setValue(fcValue);
  }
}
